<template>
  <div class="club-search-select">
    <div class="club-search-select__section">
      <div
        class="club-search-select__combo"
        :class="{ 'club-search-select__combo--list-open': listPanelVisible }"
      >
        <div
          class="club-search-select__combo-input"
          :class="{ 'club-search-select__combo-input--error': !!error }"
        >
          <USInputText
            v-model="searchQuery"
            input-id="club-search-select-query"
            name="club-search-select-query"
            placeholder="Start typing a club name..."
            autocomplete="off"
            aria-autocomplete="list"
            aria-controls="club-search-select-list"
            :aria-expanded="listPanelVisible ? 'true' : 'false'"
          />
          <USIcon
            v-if="hasValidPickedSelection"
            name="checkmark"
            color="var(--color--primary-b1)"
            css-diameter="18px"
            class="club-search-select__picked-icon"
            aria-hidden="true"
          />
        </div>
        <div
          v-show="listPanelVisible"
          id="club-search-select-list"
          ref="scrollRoot"
          class="club-search-select__scroll"
          role="listbox"
          aria-label="Clubs"
          :aria-busy="loadingInitial"
        >
      <USIcon
        v-if="loadingInitial"
        name="spinner"
        css-diameter="28px"
        class="club-search-select__spinner club-search-select__spinner--center"
      />
      <template v-else>
        <p
          v-if="emptyMessage"
          class="club-search-select__empty body--p2--regular"
        >
          {{ emptyMessage }}
        </p>
        <ul v-else-if="clubs.length" class="club-search-select__list">
          <li
            v-for="club in clubs"
            :key="club.id"
            class="club-search-select__list-item"
          >
            <button
              type="button"
              role="option"
              :aria-selected="modelValue === club.id"
              class="club-search-select__option body--p2--regular"
              :class="{
                'club-search-select__option--active': modelValue === club.id
              }"
              @click="selectClub(club)"
            >
              {{ club.clubName
              }}<span v-if="club.city" class="club-search-select__city">
                — {{ club.city }}</span
              >
            </button>
          </li>
        </ul>
        <USIcon
          v-if="loadingMore"
          name="spinner"
          css-diameter="22px"
          class="club-search-select__spinner club-search-select__spinner--more"
        />
      </template>
          <div
            ref="scrollSentinel"
            class="club-search-select__sentinel"
            aria-hidden="true"
          />
        </div>
        <p
          v-if="error"
          id="club-search-select-error"
          class="club-search-select__field-error body--p2--regular"
          role="alert"
        >
          {{ error }}
        </p>
      </div>

      <div
        v-if="userStore.isOperator"
        class="club-search-select__operator-filter margin-top--5"
      >
        <USInputCheckbox
          v-model="myClubsOnly"
          input-id="club-search-my-clubs-only"
          name="club-search-my-clubs-only"
          label="Show my clubs only"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import { useClient } from '@base/api/useClient'
import { LIST_CLUBS_FOR_CLUB_PICKER_QUERY } from '@base/api/queries/listClubsForClubPicker'
import USIcon from '@base/components/USIcon/USIcon.vue'
import USInputCheckbox from '@base/components/USInputCheckbox/USInputCheckbox.vue'
import USInputText from '@base/components/USInputText/USInputText.vue'
import { useUserStore } from '@base/store/userStore'

import { listClubsForClubPicker } from '__generated__/listClubsForClubPicker'
import { ClubSortField, ListClubsFilter, SortOrder } from '__generated__/globalTypes'

const PAGE_SIZE = 50

const props = withDefaults(
  defineProps<{
    modelValue: string | null
    /** When set, search field shows error border; message renders below the list panel. */
    error?: string
    /**
     * When the parent knows the club up front (e.g. add-event from a club page), syncs the
     * input label, selection, and non-golf flag. User can still edit the search to pick another club.
     * Primitives avoid unstable object identity in the parent computed.
     */
    presetClubId?: string | null
    presetClubName?: string | null
    presetClubIsNonGolf?: boolean | null
  }>(),
  {
    error: '',
    presetClubId: null,
    presetClubName: null,
    presetClubIsNonGolf: null
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void
  /** Mirrors selected club’s `isNonGolf` for create-event; `null` when no club is selected. */
  (e: 'update:clubIsNonGolf', value: boolean | null): void
}>()

const userStore = useUserStore()
const client = useClient()

const searchQuery = ref('')
const myClubsOnly = ref(true)

const clubs = ref<
  { id: string; clubName: string; city?: string | null; isNonGolf: boolean }[]
>([])
const totalCount = ref(0)
const loadingInitial = ref(true)
const loadingMore = ref(false)
const loadError = ref(false)

const selectionMeta = ref<{
  id: string
  clubName: string
  isNonGolf: boolean
} | null>(null)

/** Exact label in the input when user commits a pick (Maps-style). */
const committedSelectionLabel = ref<string | null>(null)

const hasValidPickedSelection = computed(
  () =>
    !!props.modelValue &&
    committedSelectionLabel.value !== null &&
    searchQuery.value === committedSelectionLabel.value
)

const listPanelVisible = computed(() => !hasValidPickedSelection.value)

const scrollRoot = ref<HTMLElement | null>(null)
const scrollSentinel = ref<HTMLElement | null>(null)
let intersectionObserver: IntersectionObserver | null = null

let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null

const hasMore = computed(
  () => totalCount.value > 0 && clubs.value.length < totalCount.value
)

const emptyMessage = computed(() => {
  if (loadingInitial.value) return ''
  if (loadError.value) {
    return 'Could not load clubs. Try again or adjust search.'
  }
  if (userStore.isOperator && myClubsOnly.value && userStore.clubs.length === 0) {
    return 'You have no clubs assigned. Turn off “Show my clubs only” to browse the directory, or contact support.'
  }
  if (clubs.value.length === 0) {
    return 'No clubs match.'
  }
  return ''
})

function buildFilter(): ListClubsFilter {
  const filter: ListClubsFilter = {}
  const q = searchQuery.value.trim()
  if (q.length >= 2) {
    filter.clubName = q
  }
  if (userStore.isOperator && myClubsOnly.value) {
    filter.clubIds = [...userStore.clubs]
  }
  return filter
}

function selectClub(club: {
  id: string
  clubName: string
  isNonGolf: boolean
}) {
  applyClubPick(club)
}

function applyClubPick(club: {
  id: string
  clubName: string
  isNonGolf: boolean
}) {
  committedSelectionLabel.value = club.clubName
  searchQuery.value = club.clubName
  selectionMeta.value = {
    id: club.id,
    clubName: club.clubName,
    isNonGolf: club.isNonGolf
  }
  if (props.modelValue !== club.id) {
    emit('update:modelValue', club.id)
  }
  emit('update:clubIsNonGolf', club.isNonGolf)
}

function syncLabelFromPresetIfMatching(id: string) {
  const pid = props.presetClubId
  const pname = props.presetClubName?.trim() ?? ''
  if (!pid || !pname || pid !== id) return
  if (
    committedSelectionLabel.value === pname &&
    searchQuery.value === pname
  ) {
    return
  }
  applyClubPick({
    id,
    clubName: pname,
    isNonGolf: props.presetClubIsNonGolf ?? false
  })
}

watch(
  () => props.modelValue,
  (id) => {
    if (!id) {
      selectionMeta.value = null
      committedSelectionLabel.value = null
      searchQuery.value = ''
      emit('update:clubIsNonGolf', null)
      return
    }
    syncLabelFromPresetIfMatching(id)
  }
)

watch(
  () =>
    [
      props.presetClubId,
      props.presetClubName,
      props.presetClubIsNonGolf
    ] as const,
  ([id, name, isNonGolf]) => {
    const trimmed = name?.trim() ?? ''
    if (!id || !trimmed) return
    applyClubPick({
      id,
      clubName: trimmed,
      isNonGolf: isNonGolf ?? false
    })
  },
  { immediate: true }
)

watch(listPanelVisible, (visible) => {
  if (!visible) {
    intersectionObserver?.disconnect()
    intersectionObserver = null
  } else {
    void nextTick(() => setupIntersectionObserver())
  }
})

async function fetchPage(append: boolean) {
  if (
    userStore.isOperator &&
    myClubsOnly.value &&
    userStore.clubs.length === 0
  ) {
    clubs.value = []
    totalCount.value = 0
    loadError.value = false
    return
  }

  if (append) {
    loadingMore.value = true
  }

  const offset = append ? clubs.value.length : 0

  try {
    loadError.value = false
    const { data } = await client.query<listClubsForClubPicker>({
      query: LIST_CLUBS_FOR_CLUB_PICKER_QUERY,
      variables: {
        filter: buildFilter(),
        sortField: ClubSortField.NAME,
        sortOrder: SortOrder.ASC,
        offset,
        limit: PAGE_SIZE
      },
      fetchPolicy: 'network-only'
    })

    if (data.listClubs.__typename !== 'ListClubsOutput') {
      if (!append) {
        clubs.value = []
        totalCount.value = 0
      }
      loadError.value = true
      return
    }

    const page = data.listClubs.clubs.map((c) => ({
      id: c.id,
      clubName: c.clubName,
      city: c.city,
      isNonGolf: c.isNonGolf
    }))

    totalCount.value = data.listClubs.count

    if (append) {
      const existing = new Set(clubs.value.map((c) => c.id))
      for (const row of page) {
        if (!existing.has(row.id)) {
          clubs.value.push(row)
          existing.add(row.id)
        }
      }
    } else {
      clubs.value = page
    }
  } catch (e) {
    console.error(e)
    loadError.value = true
    if (!append) {
      clubs.value = []
      totalCount.value = 0
    }
  } finally {
    if (append) {
      loadingMore.value = false
    }
  }
}

async function resetAndLoad() {
  loadingInitial.value = true
  clubs.value = []
  totalCount.value = 0
  try {
    await fetchPage(false)
  } finally {
    loadingInitial.value = false
    await nextTick()
    setupIntersectionObserver()
  }
}

async function loadNextPage() {
  if (!hasMore.value || loadingMore.value || loadingInitial.value) return
  await fetchPage(true)
  await nextTick()
  setupIntersectionObserver()
}

function setupIntersectionObserver() {
  intersectionObserver?.disconnect()
  intersectionObserver = null

  if (!listPanelVisible.value) return
  if (!scrollRoot.value || !scrollSentinel.value) return
  if (!hasMore.value) return

  intersectionObserver = new IntersectionObserver(
    (entries) => {
      const hit = entries.some((e) => e.isIntersecting)
      if (!hit) return
      if (loadingInitial.value || loadingMore.value) return
      if (!hasMore.value) return
      void loadNextPage()
    },
    {
      root: scrollRoot.value,
      rootMargin: '120px',
      threshold: 0
    }
  )

  intersectionObserver.observe(scrollSentinel.value)
}

watch(searchQuery, (newVal) => {
  if (
    committedSelectionLabel.value !== null &&
    props.modelValue &&
    newVal !== committedSelectionLabel.value
  ) {
    committedSelectionLabel.value = null
    selectionMeta.value = null
    emit('update:modelValue', null)
    emit('update:clubIsNonGolf', null)
  }

  if (
    committedSelectionLabel.value !== null &&
    props.modelValue &&
    newVal === committedSelectionLabel.value
  ) {
    return
  }

  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
  const trimmed = searchQuery.value.trim()
  if (trimmed.length >= 2) {
    searchDebounceTimer = setTimeout(() => {
      void resetAndLoad()
    }, 300)
  } else {
    void resetAndLoad()
  }
})

watch(myClubsOnly, () => {
  void resetAndLoad()
})

onMounted(() => {
  if (hasValidPickedSelection.value) return
  void resetAndLoad()
})

onUnmounted(() => {
  intersectionObserver?.disconnect()
  intersectionObserver = null
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
})
</script>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';

.club-search-select {
  text-align: left;

  /** Border is on the input + list so hover/focus matches USInputText (full box). */
  &__combo {
    background: transparent;
    border: none;
    overflow: visible;
  }

  &__combo--list-open &__combo-input :deep(input[type='text']) {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &__combo-input {
    position: relative;
    width: 100%;

    :deep(.us-input--text) {
      width: 100%;
    }

    :deep(.us-input--text label) {
      margin: 0;
    }

    :deep(input[type='text']) {
      background-color: var(--color--neutral-a5);
      border: var(--line-weight--light) solid var(--color--neutral-a3);
      border-radius: var(--border-radius--small);
      padding: var(--spacing--7);

      @media (min-width: breakpoint.$breakpoint--tablet) {
        padding: 16px 40px !important;
      }
    }

    :deep(input[type='text']:hover),
    :deep(input[type='text']:focus) {
      border: var(--line-weight--heavy) solid var(--color--primary-b1) !important;
      border-radius: var(--border-radius--small) !important;
    }

    &--error {
      :deep(input[type='text']) {
        border-color: var(--color--semantic--error-2) !important;
      }

      :deep(input[type='text']:hover),
      :deep(input[type='text']:focus) {
        border: var(--line-weight--heavy) solid var(--color--semantic--error-2) !important;
      }
    }
  }

  &__combo--list-open &__combo-input :deep(input[type='text']:hover),
  &__combo--list-open &__combo-input :deep(input[type='text']:focus) {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  &__combo--list-open &__combo-input--error :deep(input[type='text']:hover),
  &__combo--list-open &__combo-input--error :deep(input[type='text']:focus) {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  &__picked-icon {
    pointer-events: none;
    position: absolute;
    right: var(--spacing--7);
    top: 50%;
    transform: translateY(-50%);

    @media (min-width: breakpoint.$breakpoint--tablet) {
      right: 40px;
    }
  }

  &__scroll {
    background: var(--color--neutral-a5);
    border: var(--line-weight--light) solid var(--color--neutral-a4);
    border-radius: 0 0 var(--border-radius--small) var(--border-radius--small);
    border-top: none;
    margin-top: 0;
    max-height: 320px;
    overflow-y: auto;
    position: relative;
  }

  &__field-error {
    color: var(--color--semantic--error-2);
    margin: var(--spacing--8) 0 0;
    padding: 0 var(--spacing--7);

    @media (min-width: breakpoint.$breakpoint--tablet) {
      padding: 0 40px;
    }
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__list-item {
    margin: 0;
    padding: 0;
    border-bottom: var(--line-weight--light) solid var(--color--neutral-a4);

    &:last-child {
      border-bottom: none;
    }
  }

  &__option {
    appearance: none;
    background: transparent;
    border: none;
    color: inherit;
    cursor: pointer;
    display: block;
    font: inherit;
    margin: 0;
    padding: var(--spacing--7);
    text-align: left;
    width: 100%;

    @media (min-width: breakpoint.$breakpoint--tablet) {
      padding: 16px 40px;
    }

    &:hover,
    &--active {
      background: var(--color--secondary-c2);
    }
  }

  &__city {
    color: var(--color--neutral-a2);
  }

  &__empty {
    margin: 0;
    padding: var(--spacing--7);
    color: var(--color--neutral-a2);

    @media (min-width: breakpoint.$breakpoint--tablet) {
      padding: 16px 40px;
    }
  }

  &__sentinel {
    height: 1px;
    width: 100%;
    pointer-events: none;
  }

  &__spinner {
    &--center {
      display: block;
      margin: var(--spacing--8) auto;
    }

    &--more {
      display: block;
      margin: var(--spacing--4) auto;
    }
  }
}
</style>
